import { HTMLAttributes, useCallback, useState } from 'react';
import { WithPopover } from './WithPopover';
import { TextOverflowEllipsis } from './TextOverflowEllipsis';

export type TextOverflowProps = HTMLAttributes<HTMLDivElement> & {
  onEllipsisChange?: (isTextOverflowed: boolean) => void;
  renderIn?: 'popover' | 'tooltip' | null;
  lineClamp?: number;
  popoverAcceptsMouse?: boolean;
  textContent?: string;
  focusable?: boolean;
  showWhitespace?: boolean;
};

/**
 * Wrapper component providing truncation with ellipsis and a popover for your content.
 * @component
 * @param textContent use this prop when you have nested elements (such as icons) contained within your to-be-truncated content, and wish to avoid rendering the text they contain.
 */
export const TextOverflow = ({
  className,
  children,
  onEllipsisChange,
  lineClamp,
  renderIn = 'popover',
  popoverAcceptsMouse = true,
  textContent,
  focusable = false,
  showWhitespace = false,
  ...rest
}: TextOverflowProps) => {
  const showPopover = renderIn === 'popover';
  const showTooltip = renderIn === 'tooltip';
  const [isTextOverflowed, setIsTextOverflowed] = useState<boolean>(false);
  const [innerText, setInnerText] = useState<string>('');

  const handleEllipsisChange = useCallback(
    (overflowed: boolean, innerText: string) => {
      const content = textContent ?? innerText;
      setIsTextOverflowed(overflowed);
      setInnerText(content);
      if (onEllipsisChange && overflowed !== isTextOverflowed) {
        onEllipsisChange(overflowed);
      }
    },
    [textContent, onEllipsisChange, isTextOverflowed]
  );

  const handleInnerTextChange = useCallback(
    (innerText: string) => {
      const content = textContent ?? innerText;
      setInnerText(content);
    },
    [textContent]
  );

  const ellipsisProps = {
    className,
    lineClamp,
    onEllipsisChange: handleEllipsisChange,
    onInnerTextChange: handleInnerTextChange,
    ...rest,
  };

  return (
    <WithPopover
      acceptsMouse={popoverAcceptsMouse}
      content={showPopover && isTextOverflowed && innerText}
    >
      <TextOverflowEllipsis
        {...(showTooltip && isTextOverflowed
          ? { 'data-tooltip-text': innerText }
          : {})}
        {...ellipsisProps}
        initialIsTextOverflowed={isTextOverflowed}
        focusable={focusable}
        showWhitespace={showWhitespace}
      >
        {children}
      </TextOverflowEllipsis>
    </WithPopover>
  );
};
